import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL || import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY || import.meta.env.VITE_SUPABASE_ANON_KEY;

console.log('Supabase 环境变量检查:', {
  url: supabaseUrl ? '已设置' : '未设置',
  key: supabaseAnonKey ? '已设置' : '未设置',
  urlValue: supabaseUrl,
  keyValue: supabaseAnonKey ? `${supabaseAnonKey.substring(0, 10)}...` : '无',
  allEnvVars: import.meta.env
});

if (!supabaseUrl || !supabaseAnonKey) {
  console.error('Supabase 环境变量缺失:', { supabaseUrl: !!supabaseUrl, supabaseAnonKey: !!supabaseAnonKey });
}

// 即使环境变量缺失也创建客户端，但会在使用时处理错误
export const supabase = supabaseUrl && supabaseAnonKey 
  ? createClient(supabaseUrl, supabaseAnonKey)
  : null;

const IMAGE_BUCKET = 'site-images';
const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const IMAGE_MIME_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp', 'image/gif']);

const getImageExtension = (mimeType: string, fileName?: string) => {
  const fileExtension = fileName?.split('.').pop()?.toLowerCase().replace(/[^a-z0-9]/g, '');
  if (fileExtension && ['jpg', 'jpeg', 'png', 'webp', 'gif'].includes(fileExtension)) {
    return fileExtension === 'jpeg' ? 'jpg' : fileExtension;
  }

  const mimeExtension = mimeType.split('/')[1]?.toLowerCase();
  return mimeExtension === 'jpeg' ? 'jpg' : mimeExtension || 'jpg';
};

// 数据类型定义
export interface PersonalInfo {
  id?: string;
  name: string;
  title: string;
  email: string;
  phone: string;
  location: string;
  summary: string;
  skills: string[];
  languages: string[];
  avatar?: string;
}

export interface EducationItem {
  id?: string;
  school: string;
  degree: string;
  major: string;
  start_date: string;
  end_date: string;
  description: string;
  gpa?: string;
  image?: string;
}

export interface ExperienceItem {
  id?: string;
  company: string;
  position: string;
  start_date: string;
  end_date: string;
  description: string;
  achievements: string[];
  image?: string;
}

export interface ProjectItem {
  id?: string;
  name: string;
  description: string;
  technologies: string[];
  start_date: string;
  end_date: string;
  github?: string;
  demo?: string;
  highlights: string[];
  image?: string;
}

export interface PaperItem {
  id?: string;
  title: string;
  journal: string;
  date: string;
  authors: string;
  abstract: string;
  keywords: string[];
  url?: string;
  image?: string;
}

export interface HomeImage {
  id?: string;
  url?: string;
  image_url?: string;
  display_order?: number;
}

// Supabase 数据操作类
export class SupabaseService {
  // 检查 Supabase 是否可用
  private isSupabaseAvailable(): boolean {
    if (!supabase) {
      console.warn('Supabase 客户端未初始化，环境变量可能缺失');
      return false;
    }
    return true;
  }

  private async uploadBlob(blob: Blob, folder: string, fileName?: string) {
    if (!this.isSupabaseAvailable()) {
      throw new Error('Supabase 客户端未初始化');
    }
    if (!IMAGE_MIME_TYPES.has(blob.type)) {
      throw new Error('仅支持 JPG、PNG、WebP 和 GIF 图片');
    }
    if (blob.size > MAX_IMAGE_SIZE) {
      throw new Error('图片大小不能超过 5MB');
    }

    const safeFolder = folder.replace(/[^a-z0-9/_-]/gi, '-') || 'misc';
    const uniqueId = typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : Date.now() + '-' + Math.random().toString(36).slice(2);
    const path = safeFolder + '/' + uniqueId + '.' + getImageExtension(blob.type, fileName);
    const { error } = await supabase!.storage
      .from(IMAGE_BUCKET)
      .upload(path, blob, {
        cacheControl: '31536000',
        contentType: blob.type,
        upsert: false,
      });

    if (error) {
      throw new Error('图片上传失败：' + error.message);
    }

    const { data } = supabase!.storage.from(IMAGE_BUCKET).getPublicUrl(path);
    if (!data.publicUrl) {
      throw new Error('无法生成图片访问地址');
    }

    return { path, url: data.publicUrl };
  }

  async uploadImage(file: File, folder = 'misc'): Promise<string> {
    const uploaded = await this.uploadBlob(file, folder, file.name);
    return uploaded.url;
  }

  async migrateLegacyImages(): Promise<{ migrated: number; failed: number }> {
    if (!this.isSupabaseAvailable()) {
      return { migrated: 0, failed: 0 };
    }

    const locations = [
      { table: 'home_images', field: 'url', folder: 'home' },
      { table: 'education', field: 'image', folder: 'education' },
      { table: 'experience', field: 'image', folder: 'experience' },
      { table: 'projects', field: 'image', folder: 'projects' },
      { table: 'papers', field: 'image', folder: 'papers' },
    ];
    let migrated = 0;
    let failed = 0;

    for (const location of locations) {
      const { data: rows, error: readError } = await supabase!
        .from(location.table)
        .select('id,' + location.field);

      if (readError) {
        console.error('读取 ' + location.table + ' 旧图片失败：', readError);
        failed += 1;
        continue;
      }

      for (const row of (rows || []) as Array<Record<string, any>>) {
        const value = row[location.field];
        if (typeof value !== 'string' || !value.startsWith('data:image/')) {
          continue;
        }

        let uploadedPath: string | null = null;
        try {
          const blob = await (await fetch(value)).blob();
          const uploaded = await this.uploadBlob(blob, 'legacy/' + location.folder);
          uploadedPath = uploaded.path;

          const { error: updateError } = await supabase!
            .from(location.table)
            .update({ [location.field]: uploaded.url })
            .eq('id', row.id);

          if (updateError) {
            throw updateError;
          }
          migrated += 1;
        } catch (error) {
          failed += 1;
          console.error('迁移 ' + location.table + ' 图片失败：', error);
          if (uploadedPath) {
            await supabase!.storage.from(IMAGE_BUCKET).remove([uploadedPath]);
          }
        }
      }
    }

    return { migrated, failed };
  }

  // 测试连接
  async testConnection(): Promise<boolean> {
    try {
      if (!this.isSupabaseAvailable()) {
        return false;
      }
      
      console.log('测试 Supabase 连接...');
      const { data, error } = await supabase!.from('personal_info').select('id').limit(1);
      if (error) {
        console.error('Supabase 连接测试失败:', error);
        console.error('错误详情:', error.message, error.details, error.hint);
        return false;
      }
      console.log('Supabase 连接成功');
      return true;
    } catch (error) {
      console.error('Supabase 连接异常:', error);
      return false;
    }
  }

  // 个人信息操作
  async getPersonalInfo(): Promise<PersonalInfo | null> {
    try {
      if (!this.isSupabaseAvailable()) {
        return null;
      }
      
      console.log('获取个人信息...');
      const { data, error } = await supabase!
        .from('personal_info')
        .select('*')
        .limit(1)
        .single();
      
      if (error) {
        if (error.code === 'PGRST116') {
          console.log('个人信息表为空，返回 null');
          return null;
        }
        console.error('获取个人信息失败:', error);
        return null;
      }
      
      console.log('获取个人信息成功:', data);
      return data;
    } catch (error) {
      console.error('获取个人信息异常:', error);
      return null;
    }
  }

  async savePersonalInfo(info: PersonalInfo): Promise<boolean> {
    try {
      if (!this.isSupabaseAvailable()) {
        return false;
      }
      
      console.log('保存个人信息:', info);
      
      // 先检查是否存在记录
      const { data: existing, error: selectError } = await supabase!
        .from('personal_info')
        .select('id')
        .limit(1);

      if (selectError) {
        console.error('查询现有个人信息失败:', selectError);
        return false;
      }

      const personalData = {
        name: info.name,
        title: info.title,
        email: info.email,
        phone: info.phone,
        location: info.location,
        summary: info.summary,
        avatar: info.avatar,
        updated_at: new Date().toISOString()
      };

      if (existing && existing.length > 0) {
        // 更新现有记录
        const { error } = await supabase!
          .from('personal_info')
          .update(personalData)
          .eq('id', existing[0].id);
        
        if (error) {
          console.error('更新个人信息失败:', error);
          console.error('错误详情:', error.message, error.details, error.hint);
          console.error('更新数据:', personalData);
          return false;
        }
        console.log('个人信息更新成功');
      } else {
        // 插入新记录
        const { error } = await supabase!
          .from('personal_info')
          .insert([personalData]);
        
        if (error) {
          console.error('插入个人信息失败:', error);
          console.error('错误详情:', error.message, error.details, error.hint);
          console.error('插入数据:', personalData);
          return false;
        }
        console.log('个人信息插入成功');
      }
      
      return true;
    } catch (error) {
      console.error('保存个人信息异常:', error);
      return false;
    }
  }

  // Honor操作
  async getEducation(): Promise<EducationItem[]> {
    try {
      if (!this.isSupabaseAvailable()) {
        return [];
      }
      
      console.log('获取Honor...');
      const { data, error } = await supabase!
        .from('education')
        .select('*')
        .order('start_date', { ascending: false });
      
      if (error) {
        console.error('获取Honor失败:', error);
        return [];
      }
      
      console.log('获取Honor成功:', data);
      return data || [];
    } catch (error) {
      console.error('获取Honor异常:', error);
      return [];
    }
  }

  async saveEducation(items: EducationItem[]): Promise<boolean> {
    try {
      if (!this.isSupabaseAvailable()) {
        return false;
      }
      
      console.log('保存Honor:', items);
      
      // 删除所有现有记录 - 使用整数ID
      const { error: deleteError } = await supabase!
        .from('education')
        .delete()
        .gte('id', 1);
      
      if (deleteError) {
        console.error('删除现有Honor失败:', deleteError);
        console.error('删除错误详情:', deleteError.message, deleteError.details, deleteError.hint);
        return false; // 删除失败，不继续插入
      }
      
      // 插入新记录
      if (items.length > 0) {
        const educationData = items.map(item => ({
          school: item.school,
          degree: item.degree,
          major: item.major,
          start_date: item.start_date,
          end_date: item.end_date,
          description: item.description,
          gpa: item.gpa || null,
          image: item.image || null
        }));

        const { error } = await supabase!
          .from('education')
          .insert(educationData);
        
        if (error) {
          console.error('插入Honor失败:', error);
          console.error('插入错误详情:', error.message, error.details, error.hint);
          console.error('插入数据:', educationData);
          return false;
        }
        console.log('Honor保存成功');
      }
      
      return true;
    } catch (error) {
      console.error('保存Honor异常:', error);
      return false;
    }
  }

  // 工作经验操作
  async getExperience(): Promise<ExperienceItem[]> {
    try {
      if (!this.isSupabaseAvailable()) {
        return [];
      }
      
      console.log('获取工作经验...');
      const { data, error } = await supabase!
        .from('experience')
        .select('*')
        .order('start_date', { ascending: false });
      
      if (error) {
        console.error('获取工作经验失败:', error);
        return [];
      }
      
      console.log('获取工作经验成功:', data);
      return data || [];
    } catch (error) {
      console.error('获取工作经验异常:', error);
      return [];
    }
  }

  async saveExperience(items: ExperienceItem[]): Promise<boolean> {
    try {
      if (!this.isSupabaseAvailable()) {
        return false;
      }
      
      console.log('保存工作经验:', items);
      
      // 删除所有现有记录 - 使用整数ID
      const { error: deleteError } = await supabase!
        .from('experience')
        .delete()
        .gte('id', 1);
      
      if (deleteError) {
        console.error('删除现有工作经验失败:', deleteError);
        console.error('删除错误详情:', deleteError.message, deleteError.details, deleteError.hint);
        return false; // 删除失败，不继续插入
      }
      
      // 插入新记录
      if (items.length > 0) {
        const experienceData = items.map(item => ({
          company: item.company,
          position: item.position,
          start_date: item.start_date,
          end_date: item.end_date,
          description: item.description,
          achievements: Array.isArray(item.achievements) ? item.achievements : [],
          image: item.image || null
        }));

        const { error } = await supabase!
          .from('experience')
          .insert(experienceData);
        
        if (error) {
          console.error('插入工作经验失败:', error);
          return false;
        }
        console.log('工作经验保存成功');
      }
      
      return true;
    } catch (error) {
      console.error('保存工作经验异常:', error);
      return false;
    }
  }

  // 项目作品操作
  async getProjects(): Promise<ProjectItem[]> {
    try {
      if (!this.isSupabaseAvailable()) {
        return [];
      }
      
      console.log('获取项目作品...');
      const { data, error } = await supabase!
        .from('projects')
        .select('*')
        .order('start_date', { ascending: false });
      
      if (error) {
        console.error('获取项目作品失败:', error);
        return [];
      }
      
      console.log('获取项目作品成功:', data);
      return data || [];
    } catch (error) {
      console.error('获取项目作品异常:', error);
      return [];
    }
  }

  async saveProjects(items: ProjectItem[]): Promise<boolean> {
    try {
      if (!this.isSupabaseAvailable()) {
        return false;
      }
      
      console.log('保存项目作品:', items);
      
      // 删除所有现有记录 - 使用整数ID
      const { error: deleteError } = await supabase!
        .from('projects')
        .delete()
        .gte('id', 1);
      
      if (deleteError) {
        console.error('删除现有项目作品失败:', deleteError);
        console.error('删除错误详情:', deleteError.message, deleteError.details, deleteError.hint);
        return false; // 删除失败，不继续插入
      }
      
      // 插入新记录
      if (items.length > 0) {
        const projectsData = items.map(item => ({
          title: item.name,
          description: item.description,
          technologies: Array.isArray(item.technologies) ? item.technologies : [],
          start_date: item.start_date,
          end_date: item.end_date,
          github_url: item.github || null,
          demo_url: item.demo || null,
          achievements: Array.isArray(item.highlights) ? item.highlights : [],
          image: item.image || null
        }));

        const { error } = await supabase!
          .from('projects')
          .insert(projectsData);
        
        if (error) {
          console.error('插入项目作品失败:', error);
          return false;
        }
        console.log('项目作品保存成功');
      }
      
      return true;
    } catch (error) {
      console.error('保存项目作品异常:', error);
      return false;
    }
  }

  // 学术成果操作
  async getPapers(): Promise<PaperItem[]> {
    try {
      if (!this.isSupabaseAvailable()) {
        return [];
      }
      
      console.log('获取学术成果...');
      const { data, error } = await supabase!
        .from('papers')
        .select('*');
      
      if (error) {
        console.error('获取学术成果失败:', error);
        return [];
      }
      
      console.log('获取学术成果成功:', data);
      return data || [];
    } catch (error) {
      console.error('获取学术成果异常:', error);
      return [];
    }
  }

  async savePapers(items: PaperItem[]): Promise<boolean> {
    try {
      if (!this.isSupabaseAvailable()) {
        return false;
      }
      
      console.log('保存学术成果:', items);
      console.log('处理后的数据:', items.map(item => ({
        title: item.title,
        journal: item.journal,
        year: item.date,
        authors: item.authors,
        abstract: item.abstract,
        keywords: Array.isArray(item.keywords) ? item.keywords : [],
        url: item.url || null,
        image: item.image || null
      })));
      
      // 删除所有现有记录 - 使用整数ID
      const { error: deleteError } = await supabase!
        .from('papers')
        .delete()
        .gte('id', 1);
      
      if (deleteError) {
        console.error('删除现有学术成果失败:', deleteError);
        console.error('删除错误详情:', deleteError.message, deleteError.details, deleteError.hint);
        return false; // 删除失败，不继续插入
      }
      
      // 插入新记录
      if (items.length > 0) {
        const papersData = items.map(item => ({
          title: item.title,
          journal: item.journal,
          year: item.date,
          authors: item.authors,
          abstract: item.abstract,
          keywords: Array.isArray(item.keywords) ? item.keywords : [],
          url: item.url || null,
          image: item.image || null
        }));

        const { error } = await supabase!
          .from('papers')
          .insert(papersData);
        
        if (error) {
          console.error('插入学术成果失败:', error);
          console.error('错误详情:', error.message, error.details, error.hint);
          console.error('插入的数据:', papersData);
          return false;
        }
        console.log('学术成果保存成功');
      }
      
      return true;
    } catch (error) {
      console.error('保存学术成果异常:', error);
      return false;
    }
  }

  // 主页图片操作
  async getHomeImages(): Promise<HomeImage[]> {
    try {
      if (!this.isSupabaseAvailable()) {
        return [];
      }
      
      console.log('获取主页图片...');
      const { data, error } = await supabase!
        .from('home_images')
        .select('*')
        .order('id', { ascending: true });
      
      if (error) {
        console.error('获取主页图片失败:', error);
        console.error('错误详情:', error.message, error.details, error.hint);
        return [];
      }
      
      console.log('获取主页图片成功，原始数据:', data);
      console.log('数据条数:', data?.length || 0);
      if (data && data.length > 0) {
        console.log('第一条数据示例:', data[0]);
      }
      return data || [];
    } catch (error) {
      console.error('获取主页图片异常:', error);
      return [];
    }
  }

  async saveHomeImages(images: string[]): Promise<boolean> {
    try {
      if (!this.isSupabaseAvailable()) {
        return false;
      }
      
      console.log('保存主页图片:', images);
      
      // 删除所有现有记录 - 使用整数ID
      const { error: deleteError } = await supabase!
        .from('home_images')
        .delete()
        .gte('id', 1);
      
      if (deleteError) {
        console.error('删除现有主页图片失败:', deleteError);
        console.error('删除错误详情:', deleteError.message, deleteError.details, deleteError.hint);
        return false; // 删除失败，不继续插入
      }
      
      // 插入新记录
      if (images.length > 0) {
        // 过滤掉空值，只保留有效的图片URL
        const validImages = images.filter(url => url && url.trim() !== '');
        
        if (validImages.length > 0) {
          const imageData = validImages.map((url, index) => ({
            url: url
          }));

          const { error } = await supabase!
            .from('home_images')
            .insert(imageData);
          
          if (error) {
            console.error('插入主页图片失败:', error);
            console.error('错误详情:', error.message, error.details, error.hint);
            console.error('插入的数据:', imageData);
            return false;
          }
          console.log('主页图片保存成功');
        } else {
          console.log('没有有效的图片URL，跳过插入');
        }
      }
      
      return true;
    } catch (error) {
      console.error('保存主页图片异常:', error);
      return false;
    }
  }

  // 获取所有数据
  async getAllData() {
    try {
      console.log('开始获取所有数据...');
      
      // 先测试连接
      const isConnected = await this.testConnection();
      if (!isConnected) {
        console.warn('Supabase 连接失败，尝试使用本地存储数据');
        // 优先使用本地存储的数据
        const savedData = localStorage.getItem('adminData');
        if (savedData) {
          try {
            const parsedData = JSON.parse(savedData);
            console.log('使用本地存储数据:', parsedData);
            return parsedData;
          } catch (error) {
            console.error('解析本地数据失败:', error);
          }
        }
        // 如果本地存储也没有数据，才使用默认数据
        console.warn('本地存储无数据，使用默认数据');
        return this.getDefaultData();
      }

      const [personalInfo, education, experience, projects, papers, homeImages] = await Promise.all([
        this.getPersonalInfo(),
        this.getEducation(),
        this.getExperience(),
        this.getProjects(),
        this.getPapers(),
        this.getHomeImages()
      ]);

      const result = {
        personalInfo: personalInfo || this.getDefaultPersonalInfo(),
        education: education.map(item => ({
          ...item,
          id: item.id || Date.now().toString(),
          startDate: item.start_date,
          endDate: item.end_date
        })),
        experience: experience.map(item => ({
          ...item,
          id: item.id || Date.now().toString(),
          startDate: item.start_date,
          endDate: item.end_date
        })),
        projects: projects.map(item => ({
          ...item,
          id: item.id || Date.now().toString(),
          startDate: item.start_date,
          endDate: item.end_date
        })),
        papers: papers.map(item => ({
          ...item,
          id: item.id || Date.now().toString()
        })),
        homeImages: (() => {
          console.log('处理主页图片，原始数据:', homeImages);
          console.log('原始数据类型:', Array.isArray(homeImages) ? 'Array' : typeof homeImages);
          console.log('原始数据长度:', Array.isArray(homeImages) ? homeImages.length : 'N/A');
          
          if (!Array.isArray(homeImages) || homeImages.length === 0) {
            console.log('主页图片为空或不是数组，返回空数组');
            return [];
          }
          
          const urls = homeImages.map((img, index) => {
            const url = (img as any)?.url || (img as any)?.image_url || '';
            console.log(`映射图片 ${index}:`, img, '-> URL:', url);
            return url;
          }).filter(url => url && url.trim() !== '');
          
          console.log('处理后的主页图片URLs:', urls);
          console.log('有效URL数量:', urls.length);
          return urls;
        })()
      };

      // 如果任何数据为null，但本地存储有数据，则使用本地存储的数据
      const savedData = localStorage.getItem('adminData');
      if (savedData) {
        try {
          const parsedData = JSON.parse(savedData);
          
          // 如果Supabase数据为空，使用本地存储的数据
          if (!personalInfo && parsedData.personalInfo) {
            result.personalInfo = parsedData.personalInfo;
          }
          if ((education.length === 0 || !education) && parsedData.education) {
            result.education = parsedData.education;
          }
          if ((experience.length === 0 || !experience) && parsedData.experience) {
            result.experience = parsedData.experience;
          }
          if ((projects.length === 0 || !projects) && parsedData.projects) {
            result.projects = parsedData.projects;
          }
          if ((papers.length === 0 || !papers) && parsedData.papers) {
            result.papers = parsedData.papers;
          }
          if ((homeImages.length === 0 || !homeImages) && parsedData.homeImages) {
            result.homeImages = parsedData.homeImages;
          }
        } catch (error) {
          console.error('解析本地数据失败:', error);
        }
      }

      console.log('获取所有数据成功:', result);
      console.log('本地存储数据检查:', {
        hasPersonalInfo: !personalInfo,
        hasEducation: education.length === 0,
        hasExperience: experience.length === 0,
        hasProjects: projects.length === 0,
        hasPapers: papers.length === 0,
        hasHomeImages: homeImages.length === 0
      });
      return result;
    } catch (error) {
      console.error('获取所有数据异常:', error);
      // 异常情况下优先使用本地存储数据
      const savedData = localStorage.getItem('adminData');
      if (savedData) {
        try {
          const parsedData = JSON.parse(savedData);
          console.log('异常情况下使用本地存储数据:', parsedData);
          return parsedData;
        } catch (parseError) {
          console.error('解析本地数据失败:', parseError);
        }
      }
      // 如果本地存储也没有数据，才使用默认数据
      console.warn('异常情况下本地存储无数据，使用默认数据');
      return this.getDefaultData();
    }
  }

  // 默认个人信息
  getDefaultPersonalInfo(): PersonalInfo {
    return {
      name: 'Yating Qiu',
      title: '计算机科学与技术应届毕业生',
      email: 'yating.qiu@example.com',
      phone: '+86 138-0000-0000',
      location: '北京市海淀区',
      summary: '即将毕业的计算机科学与技术专业学生，在校期间积极参与各类项目实践和实习，掌握多种编程语言和开发框架。具备扎实的理论基础和实践经验，热爱技术创新，渴望在软件开发领域开启职业生涯，为企业创造价值。',
      skills: ['Java', 'Python', 'JavaScript', 'React', 'Spring Boot', 'MySQL', 'Git', 'Linux', 'HTML/CSS', 'Node.js'],
      languages: ['中文 (母语)', '英语 (CET-6)', '日语 (N3)'],
      avatar: 'https://readdy.ai/api/search-image?query=Young%20fresh%20graduate%20student%20in%20casual%20business%20attire%2C%20confident%20smile%2C%20university%20campus%20background%2C%20natural%20lighting%2C%20professional%20headshot%20photography%20for%20job%20applications&width=400&height=400&seq=graduate_avatar&orientation=squarish'
    };
  }

  // 默认数据
  getDefaultData() {
    return {
      personalInfo: this.getDefaultPersonalInfo(),
      education: [
        {
          id: '1',
          school: '北京理工大学',
          degree: '本科',
          major: '计算机科学与技术',
          startDate: '2020-09',
          endDate: '2024-06',
          description: '主修计算机科学与技术专业，学习了数据结构、算法设计、操作系统、数据库系统等核心课程。',
          gpa: '3.8/4.0'
        }
      ],
      experience: [
        {
          id: '1',
          company: '腾讯科技',
          position: '前端开发实习生',
          startDate: '2023-07',
          endDate: '2023-09',
          description: '参与微信小程序开发，负责用户界面设计和交互功能实现。',
          achievements: ['完成3个小程序页面开发', '优化页面加载速度30%', '获得导师好评']
        }
      ],
      projects: [
        {
          id: '1',
          name: '在线学习管理系统',
          description: '基于Spring Boot和React开发的在线学习平台，支持课程管理、作业提交、成绩查询等功能。',
          technologies: ['Spring Boot', 'React', 'MySQL', 'Redis'],
          startDate: '2023-03',
          endDate: '2023-06',
          github: 'https://github.com/yating/learning-system',
          demo: 'https://learning-demo.com',
          highlights: ['支持1000+并发用户', '实现实时消息推送', '获得校级优秀项目奖']
        }
      ],
      papers: [
        {
          id: '1',
          title: '基于深度学习的图像识别算法研究',
          journal: '毕业论文',
          date: '2024-05',
          authors: 'Yating Qiu',
          abstract: '本文研究了基于卷积神经网络的图像识别算法，通过改进网络结构和训练方法，提高了图像分类的准确率。实验结果表明，改进后的算法在CIFAR-10数据集上的准确率达到95.2%。',
          keywords: ['深度学习', '图像识别', '卷积神经网络', '计算机视觉'],
          url: 'https://example.com/paper1.pdf'
        }
      ],
      homeImages: []
    };
  }

  // 保存所有数据
  async saveAllData(data: any): Promise<boolean> {
    try {
      console.log('开始保存所有数据:', data);
      
      // 先测试连接
      const isConnected = await this.testConnection();
      if (!isConnected) {
        console.error('Supabase 连接失败，无法保存数据');
        return false;
      }

      const results = await Promise.all([
        this.savePersonalInfo(data.personalInfo),
        this.saveEducation((data.education?.map((item: any) => ({
          ...item,
          start_date: item.startDate,
          end_date: item.endDate
        })) ) || []),
        this.saveExperience((data.experience?.map((item: any) => ({
          ...item,
          start_date: item.startDate,
          end_date: item.endDate
        })) ) || []),
        this.saveProjects((data.projects?.map((item: any) => ({
          ...item,
          start_date: item.startDate,
          end_date: item.endDate
        })) ) || []),
        this.savePapers((data.papers?.map((item: any) => ({
          ...item,
          date: item.date || item.year
        })) ) || []),
        this.saveHomeImages(data.homeImages || [])
      ]);

      const success = results.every(result => result === true);
      console.log('保存所有数据结果:', success);
      return success;
    } catch (error) {
      console.error('保存所有数据异常:', error);
      return false;
    }
  }
}

// 创建服务实例
export const supabaseService = new SupabaseService();